<template>
  <div>
    <div class="grid grid-cols-2 items-center mb-4">
      <div class="w-full mb-2 text-stone-700 dark:text-stone-300">
        {{ t('optionsdialog.Image in the center of the wheel') }}
      </div>
      <div class="w-full relative">
        <!-- Custom dropdown -->
        <div class="relative" ref="rootEl">
          <!-- Dropdown trigger button -->
          <button @click="toggleDropdown"
            class="flex items-center px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md shadow-sm bg-white dark:bg-stone-700 text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            <img :src="imageSrc" class="h-[30px] w-[30px] rounded-full mr-2" alt="Selected center image">
            <i :class="['fas ml-2', isOpen ? 'fa-caret-up' : 'fa-caret-down']"></i>
          </button>

          <!-- Dropdown menu with smart positioning -->
          <div v-if="isOpen" :class="dropdownClasses"
            class="absolute z-10 w-64 rounded-md shadow-lg bg-white dark:bg-stone-800 ring-1 ring-black ring-opacity-5 overflow-auto max-h-[300px]">
            <div class="py-1 divide-y divide-stone-200 dark:divide-stone-700">
              <!-- Remove image option -->
              <div v-if="!imageSrc.includes('none')" class="px-4 py-2">
                <button @click="removeImage"
                  class="inline-flex items-center px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md shadow-sm bg-white dark:bg-stone-700 text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-600 focus:outline-none">
                  <i class="fas fa-ban mr-2"></i>
                  {{ t('optionsdialog.Remove image') }}
                </button>
              </div>

              <!-- Upload option -->
              <div class="px-4 py-2">
                <label
                  class="cursor-pointer inline-flex items-center px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md shadow-sm bg-white dark:bg-stone-700 text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-600">
                  <i class="fas fa-upload mr-2"></i>
                  {{ t('optionsdialog.Upload image') }}
                  <input type="file" class="hidden" accept="image/*" @change="onFileChange">
                </label>
              </div>

              <!-- Text input option -->
              <div class="px-4 py-2">
                <div class="flex items-center">
                  <input type="text" v-model="myCenterText" :placeholder="t('optionsdialog.Center text')" maxlength="20"
                    @keyup.enter="setText"
                    class="flex-grow px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-l-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-stone-700 dark:text-stone-100">
                  <button @click="setText"
                    class="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-r-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                    {{ t('common.Save') }}
                  </button>
                </div>
              </div>

              <!-- Gallery images -->
              <div class="py-1">
                <div v-for="pic in galleryPictures" :key="pic.fileName" @click="selectGalleryImage(pic)"
                  class="flex items-center px-4 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 cursor-pointer">
                  <img :src="pic.fileName" class="h-[30px] w-[30px] rounded-full mr-2" :alt="pic.title">
                  <span>{{ pic.title }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import * as Util from '@utils/Util.js';
import * as ImageUtil from '@utils/ImageUtil.js';
import galleryImageList from './galleryImageList.js';

const { t } = useI18n();
const instance = getCurrentInstance();
const emit = defineEmits(['set-center-no-image', 'set-center-text', 'set-center-gallery-image', 'set-center-uploaded-image', 'set-color-values']);
const props = defineProps({
  imageSrc: String,
  centerText: String,
  isAdvanced: Boolean
});

const isOpen = ref(false);
const myCenterText = ref(props.centerText);
const galleryPictures = galleryImageList.list;
const rootEl = ref(null);

const dropdownClasses = computed(() => 'mt-2');

watch(() => props.centerText, (val) => {
  myCenterText.value = val;
});

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}
function clickOutside(event) {
  if (isOpen.value && rootEl.value && !rootEl.value.contains(event.target)) {
    isOpen.value = false;
  }
}
function removeImage() {
  isOpen.value = false;
  myCenterText.value = '';
  emit('set-center-no-image');
}
function setText() {
  isOpen.value = false;
  emit('set-center-text', myCenterText.value);
}
function onTextChange() {
  emit('set-center-text', myCenterText.value);
}
function selectGalleryImage(pic) {
  myCenterText.value = '';
  emit('set-center-gallery-image', pic.fileName);
  isOpen.value = false;
}
async function onFileChange(event) {
  const file = event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = async (e) => {
    Util.trackEvent('Wheel', 'UploadedCustomImage', '');
    const dataUri = await ImageUtil.optimizeCenterImage(e.target.result);
    emit('set-center-uploaded-image', { fileName: file.name, dataUri });
    event.target.value = '';
    if (!props.isAdvanced) {
      const ui = instance?.appContext.config.globalProperties.$ui || window.$ui;
      const ok = await ui?.dialog.confirm({
        title: t('optionsdialog.Image in the center of the wheel'),
        message: t('optionsdialog.Your image has been uploaded', { fileName: file.name }),
        cancelText: t('optionsdialog.No'),
        confirmText: t('optionsdialog.Yes'),
        type: 'is-info',
        hasIcon: true,
      });
      if (ok) await setColorsFromCustomPicture(dataUri);
    }
    isOpen.value = false;
  };
  reader.readAsDataURL(file);
}
async function setColorsFromCustomPicture(dataUri) {
  Util.trackEvent('Wheel', 'SetColorsFromCustomPicture', '');
  const colors = await ImageUtil.extractColors(dataUri);
  emit('set-color-values', colors);
}

onMounted(() => document.addEventListener('click', clickOutside));
onBeforeUnmount(() => document.removeEventListener('click', clickOutside));
</script>
